import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { langCode } from '../AllProjects/projectHelpers';
import {
    fetchLayoutRiskReport,
    runLayoutRiskPreflight,
    autofitFrame,
    applyLayoutFix,
    discardDraft,
    clearError,
    selectReport,
} from '../../../slices/layoutRiskSlice';
import '../../DashboardHome/layout-risk.css';

const LANG_FULL = {
    de: 'German', fi: 'Finnish', fr: 'French', es: 'Spanish', it: 'Italian',
    pt: 'Portuguese', nl: 'Dutch', sv: 'Swedish', da: 'Danish', no: 'Norwegian',
    pl: 'Polish', ru: 'Russian', ja: 'Japanese', zh: 'Chinese', ko: 'Korean',
    ar: 'Arabic', tr: 'Turkish', cs: 'Czech', hu: 'Hungarian', el: 'Greek', he: 'Hebrew',
};
const langName = (code) => LANG_FULL[(code || '').toLowerCase().split(/[-_]/)[0]] || (code || '').toUpperCase();
const pctLabel = (factor) => {
    if (typeof factor !== 'number') return '';
    const pct = Math.round((factor - 1) * 100);
    return pct >= 0 ? `+${pct}%` : `${pct}%`;
};

const SUPPORTED = new Set(['.idml', '.docx', '.pptx', '.xlsx', 'idml', 'docx', 'pptx', 'xlsx']);
const isSupported = (project) => SUPPORTED.has((project.originalFileType || '').toLowerCase());

const I = {
    warn: (<svg viewBox="0 0 14 14" fill="none"><path d="M7 2L1.5 11.5h11L7 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="M7 5.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><circle cx="7" cy="10" r="0.6" fill="currentColor" /></svg>),
    check: (<svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7l3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
    bolt: (<svg viewBox="0 0 14 14" fill="none"><path d="M8 1.5L3 7.5h3.5L5.5 12.5l5-6H7l1-5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>),
    refresh: (<svg viewBox="0 0 14 14" fill="none"><path d="M11.5 3.5a5 5 0 10.8 4.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><path d="M11.5 1.5v2.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>),
};

// Display caps for the capacity bar (matches the prototype's 1.32 ceiling).
const BAR_MAX = 1.32;

// Status as displayed (an applied fix reads as "fixed").
const displayStatus = (frame) => (frame.fix ? 'fixed' : frame.status);

const FrameItem = ({ frame, projectId, lang, selected, onSelect }) => {
    const dispatch = useDispatch();
    const draft = useSelector((s) => s.layoutRisk.drafts[frame.frameId]);
    const fitting = useSelector((s) => !!s.layoutRisk.fitting[frame.frameId]);
    const applying = useSelector((s) => !!s.layoutRisk.applying[frame.frameId]);

    const done = !!frame.fix;
    const status = displayStatus(frame);
    const cap = frame.capacityChars || 1;
    const fillRatio = done
        ? Math.min((frame.fix.chars || frame.predictedChars) / cap, BAR_MAX)
        : Math.min(frame.predictedChars / cap, BAR_MAX);

    return (
        <div
            className={`lr-it ${done ? 'done' : ''} ${selected ? 'sel' : ''}`}
            onClick={() => onSelect && onSelect(frame.frameId)}
        >
            <div className="lr-it-h">
                <span className="nm">{frame.label}</span>
                {frame.page != null && <span className="pg">p.{frame.page}</span>}
                <span className={`lr-pill ${status}`}>
                    {done ? (<>{I.check} fits</>)
                        : status === 'over' ? (<>{I.warn} won&apos;t fit</>)
                            : 'tight fit'}
                </span>
            </div>

            <div className="lr-bar">
                <span className={`fill ${status}`} style={{ width: `${(fillRatio / BAR_MAX) * 100}%` }} />
                <span className="capline" style={{ left: `${(1 / BAR_MAX) * 100}%` }} />
            </div>

            {done ? (
                <div className="lr-it-meta">{I.check} Auto-fit applied — {frame.fix.chars} of {frame.capacityChars} chars</div>
            ) : (
                <div className="lr-it-meta">≈ {frame.predictedChars} chars predicted · {frame.capacityChars} fit the frame</div>
            )}

            {!done && !draft && (
                <div className="lr-it-actions">
                    <button
                        type="button"
                        className="lr-afit"
                        disabled={fitting}
                        onClick={() => dispatch(autofitFrame({ projectId, frameId: frame.frameId, targetLanguage: lang }))}
                    >
                        {fitting ? (<><span className="lr-spin" />Fitting…</>) : (<>{I.bolt}Auto-fit</>)}
                    </button>
                </div>
            )}

            {!done && draft && (
                <div className="lr-fix">
                    <div className="lr-fix-l">Fit-constrained draft · {langName(lang)}</div>
                    <div className="lr-fix-t">{draft.text}</div>
                    <span className={`lr-fix-m ${draft.overBudget ? 'warn' : 'ok'}`}>
                        {draft.overBudget ? I.warn : I.check}
                        {draft.overBudget
                            ? `Still over — ${draft.chars} of ${draft.capacityChars} chars, needs a human`
                            : `Fits — ${draft.chars} of ${draft.capacityChars} chars`}
                        {draft.glossaryKept && draft.glossaryKept.length ? ' · glossary terms kept' : ''}
                    </span>
                    <div className="lr-fix-a">
                        <button
                            type="button"
                            className="lr-afit"
                            disabled={applying}
                            onClick={() => dispatch(applyLayoutFix({ projectId, frameId: frame.frameId, targetLanguage: lang, text: draft.text }))}
                        >
                            {applying ? 'Applying…' : 'Apply'}
                        </button>
                        <button type="button" className="lr-afit quiet" onClick={() => dispatch(discardDraft({ frameId: frame.frameId }))}>
                            Discard
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const hasGeometry = (frames) => frames.some((f) => f.geometry && Array.isArray(f.geometry.boxes) && f.geometry.boxes.length);

// One frame drawn at its true position/size on the scaled page. The fill level
// = predicted / capacity (from the bottom); a hatched band at the top shows
// spillover when predicted exceeds capacity.
const MapFrame = ({ frame, box, scale, showLabel, selected, onSelect }) => {
    const status = displayStatus(frame);
    const reflow = frame.overflowBehavior === 'reflow' || frame.capacityChars == null;
    const cap = frame.capacityChars || 1;
    const shown = frame.fix ? (frame.fix.chars || frame.predictedChars) : frame.predictedChars;
    const ratio = shown / cap;
    const fillPct = reflow ? 0 : Math.min(ratio, 1) * 100;
    const overPct = reflow ? 0 : Math.min(Math.max(ratio - 1, 0), 0.5) * 100;

    const title = reflow
        ? `${frame.label} — reflows`
        : `${frame.label} — ≈ ${shown} / ${frame.capacityChars} chars (${status})`;

    return (
        <div
            className={`lr-mf ${status} ${reflow ? 'reflow' : ''} ${selected ? 'sel' : ''}`}
            style={{
                left: box.x * scale,
                top: box.y * scale,
                width: Math.max(box.w * scale, 6),
                height: Math.max(box.h * scale, 6),
            }}
            title={title}
            onClick={(e) => { e.stopPropagation(); onSelect && onSelect(frame.frameId); }}
        >
            {!reflow && fillPct > 0 && <span className={`lvl ${status}`} style={{ height: `${fillPct}%` }} />}
            {overPct > 0 && <span className="ovf" style={{ height: `${Math.max(overPct, 12)}%` }} />}
            {status !== 'fit' && <span className={`dot ${status}`} />}
            {showLabel && <span className="lbl">{frame.label.replace(/ — p\.\d+$/, '')}</span>}
        </div>
    );
};

// True-to-layout map: frames placed at their real positions on a scaled page.
// Always shown (incl. no risk) — it's the proof the pre-flight ran and where
// each frame sits.
const DocumentPreview = ({ frames, selected, onSelect }) => {
    const MAX_H = 620;   // height cap so a tall portrait page can't dominate
    const MIN_PAGE = 240; // below this, multi-page rows wrap instead of shrinking
    const GAP = 22;

    // Measure the available stage width so pages scale up to fill it.
    const pagesRef = useRef(null);
    const [availW, setAvailW] = useState(0);
    useEffect(() => {
        const el = pagesRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0] && entries[0].contentRect && entries[0].contentRect.width;
            if (w) setAvailW(w);
        });
        ro.observe(el);
        setAvailW(el.clientWidth);
        return () => ro.disconnect();
    }, []);

    const groups = {};
    frames.filter((f) => f.geometry && f.geometry.boxes && f.geometry.boxes.length).forEach((f) => {
        const key = f.page == null ? '__doc__' : String(f.page);
        (groups[key] = groups[key] || []).push(f);
    });
    const keys = Object.keys(groups).sort((a, b) => {
        const na = Number(a);
        const nb = Number(b);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
        return a.localeCompare(b);
    });
    const labelFor = (key) => {
        if (key === '__doc__') return 'Document';
        if (!Number.isNaN(Number(key))) return `Page ${key}`;
        return key;
    };

    // Width each page gets: fill the stage for a single page; for several, split
    // the row evenly but never below MIN_PAGE (they wrap instead).
    const n = keys.length || 1;
    const stageW = availW || 880;
    const ideal = (stageW - GAP * (n - 1)) / n;
    const perPageW = n > 1 ? Math.max(ideal, MIN_PAGE) : ideal;

    return (
        <div className="lr-stage-wrap">
            <div className="lr-stage-h">
                <b>Layout map</b>
                <span>frames at their real position · fill = predicted text</span>
            </div>
            <div className="lr-pages" ref={pagesRef}>
                {keys.map((key) => {
                    const pageFrames = groups[key];
                    const geo = pageFrames[0].geometry;
                    const pageW = geo.pageW || 1;
                    const pageH = geo.pageH || 1;
                    const scale = Math.min(perPageW / pageW, MAX_H / pageH);
                    return (
                        <div key={key} className="lr-pg-col">
                            <span className="lr-pg-label">{labelFor(key)}</span>
                            <div className="lr-pg map" style={{ width: pageW * scale, height: pageH * scale }} onClick={() => onSelect && onSelect(null)}>
                                {pageFrames.flatMap((f) => f.geometry.boxes.map((box, bi) => (
                                    <MapFrame
                                        key={`${f.frameId}:${bi}`}
                                        frame={f}
                                        box={box}
                                        scale={scale}
                                        showLabel={box.w * scale > 64 && box.h * scale > 22}
                                        selected={selected === f.frameId}
                                        onSelect={onSelect}
                                    />
                                )))}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="lr-legend">
                <span><i className="fit" />fits</span>
                <span><i className="tight" />tight</span>
                <span><i className="over" />won&apos;t fit</span>
                <span><i className="fixed" />fixed</span>
            </div>
        </div>
    );
};

const LayoutRiskTab = ({ project, languages }) => {
    const dispatch = useDispatch();

    // Each target language is its own sub-project; run/fetch are keyed by that
    // sub-project's id + code. Fall back to the primary project for single-target.
    const langs = useMemo(() => {
        if (languages && languages.length) {
            return languages.map((l) => ({ code: langCode(l.code) || l.code, projectId: l.projectId || project._id }));
        }
        return [{ code: project.targetLanguage, projectId: project._id }];
    }, [languages, project]);

    const [active, setActive] = useState(() => (langs[0] ? langs[0].code : project.targetLanguage));
    const [selected, setSelected] = useState(null); // frameId highlighted across stage + panel
    const [showMapPref, setShowMapPref] = useState(true); // user toggle for the layout map
    const current = langs.find((l) => l.code === active) || langs[0];

    const running = useSelector((s) => s.layoutRisk.running);
    const report = useSelector((s) => (current ? selectReport(s, current.projectId, current.code) : null));
    const reportsByKey = useSelector((s) => s.layoutRisk.reportsByKey);
    const error = useSelector((s) => s.layoutRisk.error);

    const supported = isSupported(project);

    // Fetch a cached report for every language once (drives the seg badges).
    useEffect(() => {
        if (!supported) return;
        langs.forEach((l) => {
            dispatch(fetchLayoutRiskReport({ projectId: l.projectId, lang: l.code }));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supported, langs.map((l) => `${l.projectId}:${l.code}`).join(',')]);

    const riskCountFor = (l) => {
        const r = reportsByKey[`${l.projectId}:${l.code}`];
        return r && r.summary ? r.summary.atRisk : null;
    };
    const overCountFor = (l) => {
        const r = reportsByKey[`${l.projectId}:${l.code}`];
        return r && r.summary ? r.summary.over : 0;
    };

    const runPreflight = () => {
        if (!current) return;
        dispatch(runLayoutRiskPreflight({ projectId: current.projectId, targetLanguage: current.code }));
    };

    if (!supported) {
        return (
            <div className="rs-card lr-panel">
                <div className="lr-blank">
                    <b>Layout risk isn&apos;t available for this format yet</b>
                    <p>
                        Pre-flight currently supports InDesign (.idml) and Word (.docx) files —
                        the formats that round-trip real frame geometry. This project is a{' '}
                        {(project.originalFileType || 'different').replace('.', '').toUpperCase()} file.
                    </p>
                </div>
            </div>
        );
    }

    const frames = (report && report.frames) || [];
    const visible = frames.filter((f) => f.overflowBehavior !== 'reflow');
    const atRisk = visible.filter((f) => !f.fix && (f.status === 'over' || f.status === 'tight'));
    const resolved = visible.filter((f) => !!f.fix);
    const summary = (report && report.summary) || {};
    const factor = report && report.expansionFactor;
    const learned = report && report.factorSource === 'learned';
    const pageGrowth = summary.pageGrowth;
    // The positional map only applies to formats with coordinates (IDML/PPTX).
    // DOCX/XLSX have none, so they get the panel-only view. `mapCapable` gates
    // whether the toggle even appears; `showMap` is the effective visibility.
    const mapCapable = hasGeometry(frames);
    const showMap = mapCapable && showMapPref;

    const autofitAll = () => {
        atRisk.forEach((f) => dispatch(autofitFrame({ projectId: current.projectId, frameId: f.frameId, targetLanguage: current.code })));
    };

    const Toolbar = (
        <div className="lr-toolbar">
            <div className="lr-seg">
                {langs.map((l) => {
                    const n = riskCountFor(l);
                    let badge = null;
                    if (n != null) {
                        if (n === 0) badge = <span className="lr-ct ok">{I.check}</span>;
                        else badge = <span className={`lr-ct ${overCountFor(l) > 0 ? 'red' : 'amber'}`}>{n}</span>;
                    }
                    return (
                        <button key={l.code} type="button" className={active === l.code ? 'on' : ''} onClick={() => { setActive(l.code); setSelected(null); }}>
                            {(l.code || '').toUpperCase()}<span className="nm">{langName(l.code)}</span>{badge}
                        </button>
                    );
                })}
            </div>
            {report && !report.gated && mapCapable && (
                <label className="lr-hl">
                    <input type="checkbox" checked={showMapPref} onChange={(e) => setShowMapPref(e.target.checked)} />
                    Layout map
                </label>
            )}
            <div className="grow" />
            {report && (
                <span className={`lr-status ${summary.atRisk ? 'warn' : 'ok'}`}>
                    {summary.atRisk ? I.warn : I.check}
                    {summary.atRisk
                        ? `${summary.atRisk} ${summary.atRisk === 1 ? 'frame' : 'frames'} at risk in ${langName(active)}`
                        : `Every frame fits in ${langName(active)}`}
                </span>
            )}
            <button type="button" className="lr-afit quiet lr-rerun" disabled={running} onClick={runPreflight}>
                {I.refresh}<span>{running ? 'Running…' : 'Re-run pre-flight'}</span>
            </button>
        </div>
    );

    return (
        <div className="lr-tab">
                <p className="rs-page-sub" style={{ marginTop: 0 }}>
                    Rush pseudo-translates every frame at each language&apos;s expansion rate — before a single
                    word is translated — so you can see exactly which layouts will break, then let Auto-fit
                    draft translations that fit the frame.
                </p>

                {Toolbar}

                {error && (
                    <div className={`lr-banner ${error.code === 'MT_LIMIT_REACHED' ? 'limit' : ''}`}>
                        {I.warn}
                        <span>
                            {error.msg}
                            {error.code === 'MT_LIMIT_REACHED' && ' Upgrade your plan or wait for the monthly reset to keep using Auto-fit.'}
                        </span>
                        <button type="button" className="lr-banner-x" onClick={() => dispatch(clearError())} aria-label="Dismiss">×</button>
                    </div>
                )}

                {!report && (
                    <div className="rs-card lr-panel">
                        <div className="lr-blank">
                            <b>No pre-flight yet for {langName(active)}</b>
                            <p>Run the layout-risk pre-flight to predict which frames will overflow once this language is translated.</p>
                            <button type="button" className="lr-afit" disabled={running} onClick={runPreflight}>
                                {running ? (<><span className="lr-spin" />Running…</>) : (<>{I.bolt}Run pre-flight</>)}
                            </button>
                        </div>
                    </div>
                )}

                {report && report.gated && (
                    <div className="rs-card lr-panel">
                        <div className="lr-panel-h">
                            <b>Pre-flight · {langName(active)}</b>
                            <span className="ct">{summary.frameCount || 0}</span>
                        </div>
                        <div className="lr-sub">
                            {langName(active)} runs ≈ {pctLabel(factor)} vs the source.{' '}
                            {summary.atRisk
                                ? `${summary.atRisk} ${summary.atRisk === 1 ? 'frame is' : 'frames are'} at risk (${summary.over} will overflow).`
                                : 'Every frame fits.'}
                        </div>
                        <div className="lr-gate">
                            <b>Per-frame detail &amp; Auto-fit are on Growth</b>
                            <p>You can see the risk counts on any plan. Upgrade to Growth to see exactly which frames break and let Auto-fit draft translations that fit.</p>
                        </div>
                    </div>
                )}

                {report && !report.gated && (() => {
                    const panel = (
                        <aside className="rs-card lr-panel">
                            <div className="lr-panel-h">
                                <b>Pre-flight · {langName(active)}</b>
                                <span className="ct">{atRisk.length + resolved.length}</span>
                                {atRisk.length > 1 && (
                                    <button type="button" className="lr-afit lr-afit-all" onClick={autofitAll}>
                                        {I.bolt}Auto-fit all ({atRisk.length})
                                    </button>
                                )}
                            </div>

                            <div className="lr-sub">
                                {langName(active)} runs ≈ {pctLabel(factor)} vs the source.{' '}
                                {summary.over > 0
                                    ? `${summary.over} ${summary.over === 1 ? 'frame' : 'frames'} will overflow without intervention.`
                                    : summary.atRisk > 0
                                        ? 'Nothing overflows yet, but a few frames are close.'
                                        : 'Plenty of room across the document.'}
                                {learned && <span className="learned"> · learned from your projects</span>}
                            </div>

                            {pageGrowth && (
                                <div className="lr-growth">
                                    {I.warn}
                                    <div>
                                        <b>Body text reflows</b>{' '}
                                        <span>{langName(active)} pushes this from {pageGrowth.from} → {pageGrowth.to} page{pageGrowth.to === 1 ? '' : 's'}.</span>
                                    </div>
                                </div>
                            )}

                            {atRisk.length === 0 && resolved.length === 0 ? (
                                <div className="lr-empty">
                                    <span className="big">{I.check}</span>
                                    <b>No layout risk</b>
                                    <p>Checked {summary.frameCount || frames.length} frame{(summary.frameCount || frames.length) === 1 ? '' : 's'} — every one has room to spare in {langName(active)}.</p>
                                </div>
                            ) : (
                                <div className="lr-items">
                                    {atRisk.length > 0 && <div className="lr-sec">At risk ({atRisk.length})</div>}
                                    {atRisk.map((f) => (
                                        <FrameItem key={f.frameId} frame={f} projectId={current.projectId} lang={current.code} selected={selected === f.frameId} onSelect={setSelected} />
                                    ))}
                                    {resolved.length > 0 && <div className="lr-sec">Resolved ({resolved.length})</div>}
                                    {resolved.map((f) => (
                                        <FrameItem key={f.frameId} frame={f} projectId={current.projectId} lang={current.code} selected={selected === f.frameId} onSelect={setSelected} />
                                    ))}
                                </div>
                            )}
                        </aside>
                    );

                    if (!showMap) return <div className="lr-single">{panel}</div>;
                    return (
                        <div className="lr-grid">
                            <DocumentPreview frames={frames} selected={selected} onSelect={setSelected} />
                            {panel}
                        </div>
                    );
                })()}
        </div>
    );
};

export default LayoutRiskTab;
